<script setup lang="ts">
import { ManyNewsQuery } from '~/queries/many/news'

const route = useRoute()
const { locale } = useI18n()

const runtimeConfig = useRuntimeConfig()

const previewSecret = runtimeConfig?.previewSecret || runtimeConfig?.public?.previewSecret
const isPreview = computed(() => route?.query?.previewSecret === previewSecret)

interface NewsEntry {
  id: string
  title: string
  slug: string
}

interface NewsEntriesData {
  docs: NewsEntry[]
  totalDocs: number
  hasNextPage: boolean
}

const { data, error: pageError } = await useAsyncData<NewsPage>(
  () => `news-page-${locale.value}`,
  () => $fetch<NewsPage>('/api/payload-gql', {
    method: 'POST',
    body: {
      query: GlobalNewsPageQuery,
      variables: {
        locale: locale.value,
        isPreview: isPreview.value,
      },
      isPreview: isPreview.value,
    },
  })
)

const { data: entries, error: entriesError } = await useAsyncData<NewsEntriesData>(
  () => `news-entries-${locale.value}`,
  () => $fetch<NewsEntriesData>('/api/payload-gql', {
    method: 'POST',
    body: {
      query: ManyNewsQuery,
      variables: {
        locale: locale.value,
        now: isPreview.value ? new Date(0).toISOString() : cacheableNow(),
        isPreview: isPreview.value,
        statuses: isPreview.value ? ['draft', 'published'] : ['published'],
      },
      isPreview: isPreview.value,
    },
  })
)

if (pageError.value || entriesError.value) {
  throw createError({
    statusCode: 500,
    statusMessage: `Error fetching page`,
  })
}

usePageSeo({
  title: () => data.value?.title || 'News',
})
</script>

<template>
  <page-layout>
    <template #header>
      <page-header
        :title="data?.title || 'News'"
        :route="{
          name: 'news',
          title: data?.title,
          slug: data?.slug,
        }"
        :include-breadcrumbs="true"
      />
    </template>

    <template #default>
      <pages-news
        v-if="data"
        :data="data"
        :entries="entries?.docs ?? []"
      />
    </template>
  </page-layout>
</template>
